/**
 * useMessageSender — Core message sending, file upload, and response processing
 * Extracted VERBATIM from SimpleCopilotPanel.tsx
 * 
 * Contains:
 *   - processFilesOnly (L836-866) — file upload API call
 *   - processTextOnly (L868-878) — text query API call
 *   - handleSendMessage (L1028-1223) — main send callback
 *   - handlePromptSelected (L1243-1245) — set input text
 *   - handleRemoveMessage (L1247-1249) — remove message by ID
 *   - Initial prompt effect (L1225-1240) — auto-send deep-link prompt
 * 
 * Risks handled:
 *   - R1: Cross-hook state — all state received from orchestrator
 *   - R3: globalUploadedFiles — writes via globalUploadedFilesRef
 *   - R5: useCallback memoization — preserved exact dependency array
 *   - R14: currentActiveFormId — setter received from orchestrator
 *   - R20: useCallback identity — same deps, no forced splitting
 * 
 * SAFE REFACTORING: No logic changes. Code copied exactly as-is.
 * Two minor mechanical changes:
 *   1. createUserMessage now takes uploadedFiles as 3rd param (was closure)
 *   2. globalUploadedFiles writes go through globalUploadedFilesRef
 */

import * as React from 'react';
import { useRef } from 'react';
import { postExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';
import { COPILOT_CONSTANTS } from '../constants';
import mockApiResponse from '../DataSource/ApiResponseFormData.json';
import type { Message } from '../interfaces';
import type { CopilotChatParams } from './CopilotChatParams';
import { normalizeApiResponse } from '../utils/responseUtils';
import { createUserMessage, createBotMessage } from '../utils/messageUtils';
import { formatAndConvertByType } from '../utils/formatAndConvertByType';
import { isHiddenFormField, normalizeBlobReference, serializeFormValues } from '../utils/genericFormTransport';
import { validateAskResponse, validateQueryLength } from '../utils/queryLimit';
import { normalizeFormFields } from '../utils/formUtils';
import { extractTicketNumber } from '../utils/ticketExtraction';
import { selfHelpButtonShown, setSelfHelpButtonShown } from '../MessageTypes/MarkdownMessage';
import type { SelfHelpFeedbackTicketContext } from '../interfaces/api';
import {
  abandonIncompleteSelfHelpFeedback,
  buildSelfHelpFeedbackFormAssociation,
  buildSelfHelpFeedbackTicketContext,
  clearPendingSelfHelpFeedbackSubmission,
  completeSelfHelpFeedbackJourney,
  emitSelfHelpFeedbackJourneyEvent,
  findActiveSelfHelpFeedbackJourney,
  getPendingSelfHelpFeedbackSubmission,
  isDirectTicketIntent,
  isIncompleteSelfHelpFeedback,
  persistPendingSelfHelpFeedbackSubmission,
} from '../utils/selfHelpFeedbackSession';
import {
  isConfirmedTicketOutcome,
  readTicketOutcome,
} from '../utils/ticketOutcome';

interface SendMessageOptions {
  selfHelpFeedbackContext?: SelfHelpFeedbackTicketContext;
  isSelfHelpTicketHandoff?: boolean;
}

/**
 * Hook for sending messages, uploading files, and processing API responses
 * Returns handleSendMessage, handlePromptSelected, handleRemoveMessage
 */
export function useMessageSender(params: CopilotChatParams) {
  const formUploadCache = useRef(new Map<File, { scope: string; reference: string }>());
  const {
    messages,
    setMessages,
    loading,
    setLoading,
    setFollowUpQuestions,
    uploadedFiles,
    setUploadedFiles,
    showExitConfirmation,
    setShowExitConfirmation,
    setCurrentFormMetadata,
    currentFormMetadata,
    setCurrentFormQuery,
    currentActiveFormId,
    setCurrentActiveFormId,
    setSubmittedFormIds,
    setPersistentFormValues,
    persistentFormValues,
    setCurrentTicketNumber,
    sessionIdRef,
    controlRef,
    scrollRef,
    lastAnchoredMessageIdRef,
    globalUploadedFilesRef,
    effectiveApi,
    userEmail,
    mcapsTelemetry,
    platformAuthClient,
    initialPrompt,
    onInitialPromptApplied,
  } = params;

  // Local ref for initial prompt guard (only used here)
  const hasAppliedInitialPromptRef = useRef(false);
  // Verbatim from SimpleCopilotPanel.tsx L836-866
  const processFilesOnly = async (files: File[], ticketId: string) => {
    if (!ticketId) {
      throw new Error('Ticket number not found to attach files.');
    }
    const fileUploadApiUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FORM_ATTACHMENT;
    const apiPayload = new FormData();
    apiPayload.append('ticketId', ticketId);
    const appName = sessionStorage.getItem('mcapsCopilot_appName');
    if (appName) apiPayload.append('appName', appName);
    files.forEach((file) => {
      apiPayload.append('file', file, file.name);
    });
    const response = await postExternalConsumptionAPI(fileUploadApiUrl, apiPayload, platformAuthClient);
    
    // Normalize attachment response for chat display
    if (response && response.status >= COPILOT_CONSTANTS.API.SUCCESS_STATUS_MIN && response.status < COPILOT_CONSTANTS.API.SUCCESS_STATUS_MAX) {
      const rawPayload = response?.data ?? {};
      const successMsg = rawPayload.success 
        ? `✅ File "${rawPayload.fileName}" attached to ticket ${ticketId} successfully.`
        : `⚠️ Failed to attach file: ${rawPayload.validationError || 'Unknown error'}`;
      return {
        ...response,
        data: {
          ...rawPayload,
          response: successMsg,
          Message: successMsg,
          format: 'text'
        }
      };
    }

    return response;
  };

  // Story 1236552: Upload files to blob during conversational form-fill, then send text to /ask
  const processFormFillFiles = async (
    files: File[], question: string, isExitConfirmation: boolean,
    requestConversationId: string, isCurrent: () => boolean
  ) => {
    const attachmentUrl = COPILOT_CONSTANTS.API.ENDPOINTS.FORM_ATTACHMENT;
    const appName = sessionStorage.getItem('mcapsCopilot_appName');
    let uploadedCount = 0;
    let failedCount = 0;
    const scope = `${requestConversationId}:${currentActiveFormId || ''}`;
    const references: string[] = [];
    const fileField = (normalizeFormFields(currentFormMetadata) || []).find(field => field.type === 'file');
    const maximum = Math.min(fileField?.validation?.maxCount ?? 1, 5);
    if (maximum > 1) {
      const saved = currentFormMetadata?.formJson?.collectedData?.[fileField?.id];
      const values = Array.isArray(saved) ? saved : [saved];
      references.push(...values.filter(value => typeof value === 'string')
        .flatMap(value => value.split(',')).filter(value => value.startsWith('blob:')));
      formUploadCache.current.forEach(entry => { if (entry.scope === scope) references.push(entry.reference); });
    }
    const existing = new Set(references);
    const pending = files.filter(file => {
      const cached = formUploadCache.current.get(file);
      return cached?.scope !== scope || !existing.has(cached.reference);
    });
    if (existing.size + pending.length > maximum) {
      throw new Error(`This field supports up to ${maximum} attachment(s). Your draft has been retained.`);
    }
    const sizeLimit = Math.min(fileField?.validation?.maxSizeMB ?? 25, 25);
    if (files.some(file => file.size > sizeLimit * 1024 * 1024)) {
      throw new Error(`This field supports files up to ${sizeLimit} MB. Your draft has been retained.`);
    }

    for (const file of files) {
      const formPayload = new FormData();
      formPayload.append('file', file, file.name);
      formPayload.append('sessionId', requestConversationId);
      formPayload.append('fieldId', fileField?.id || 'uploadTemplate');
      if (appName) formPayload.append('appName', appName);
      try {
        const cached = formUploadCache.current.get(file);
        const res = cached?.scope === scope ? { data: { success: true, blobReference: cached.reference } }
          : await postExternalConsumptionAPI(attachmentUrl, formPayload, platformAuthClient);
        if (!isCurrent()) return undefined;
        if (res?.data?.success && res.data.blobReference) {
          uploadedCount++;
          // Extract formInstanceId from blobReference (same as form mode)
          const reference = normalizeBlobReference(res.data.blobReference);
          references.push(reference);
          if (maximum === 1) {
            formUploadCache.current.forEach((entry, prior) => {
              if (entry.scope === scope && prior !== file) formUploadCache.current.delete(prior);
            });
          }
          formUploadCache.current.set(file, { scope, reference });
        } else {
          failedCount++;
        }
      } catch (err) {
        if (!isCurrent()) return undefined;
        failedCount++;
      }
      if (failedCount) throw new Error('One or more attachments could not be uploaded. Your draft has been retained. Please try again.');
    }

    // Send text + blob ref to /ask (matching form mode: append blob ref to query)
    if (question.trim()) {
      let queryWithBlob = question;
      if (references.length) {
        queryWithBlob = `${question},"${Array.from(new Set(references)).join(',').replace(/"/g, '""')}"`;
      }
      validateQueryLength(queryWithBlob);
      const askAppName = sessionStorage.getItem('mcapsCopilot_appName');
      const apiPayload = {
        query: queryWithBlob,
        sessionId: requestConversationId,
        ...(isExitConfirmation && { cancelForm: true }),
        ...(askAppName && { appName: askAppName })
      };
      const response = await postExternalConsumptionAPI(effectiveApi, apiPayload, platformAuthClient);
      return response;
    }

    // Files only, no text — return a synthetic success response
    return {
      status: 200,
      data: {
        response: uploadedCount > 0 ? `📎 ${uploadedCount} file(s) uploaded successfully.` : `⚠️ Failed to upload files.`,
        Message: uploadedCount > 0 ? `📎 ${uploadedCount} file(s) uploaded successfully.` : `⚠️ Failed to upload files.`,
        format: 'text'
      }
    };
  };

  // Verbatim from SimpleCopilotPanel.tsx L868-878
  const processTextOnly = async (
    question: string,
    cancelForm: boolean = false,
    selfHelpFeedbackContext?: SelfHelpFeedbackTicketContext
  ) => {
  const appName = sessionStorage.getItem('mcapsCopilot_appName');
  const fields = normalizeFormFields(currentFormMetadata) || [];
  const marked = cancelForm && fields.some(isHiddenFormField)
    ? serializeFormValues(fields, persistentFormValues) : '';
  const query = cancelForm
    ? (marked.length <= COPILOT_CONSTANTS.INPUT_LIMITS.MAX_CHARACTER_COUNT ? marked : '')
    : question;
  validateQueryLength(query);
  const apiPayload = {
    query,
    sessionId: sessionIdRef.current,
    ...(cancelForm && { cancelForm: true }),
    ...(selfHelpFeedbackContext && { selfHelpFeedbackContext }),
    ...(appName && { appName: appName })
  };
    const response = await postExternalConsumptionAPI(effectiveApi, apiPayload, platformAuthClient);
    return response;
  };

  // Verbatim from SimpleCopilotPanel.tsx L1028-1223
  // R5: Exact useCallback wrapper preserved with same dependency array
  const handleSendMessage = React.useCallback(async (
    question: string,
    options: SendMessageOptions = {}
  ): Promise<boolean> => {
    try {
      validateQueryLength(question);
    } catch (error: any) {
      controlRef.current?.setInputText(question);
      setMessages(prev => [...prev, {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
        role: 'error', content: error.message, timestamp: Date.now(),
      }]);
      return false;
    }
    // Read the live session at send start: reset rotates the ref without re-rendering this hook.
    // isCurrent() fences async results from a conversation reset mid-request.
    const requestConversationId = sessionIdRef.current;
    const isCurrent = () => sessionIdRef.current === requestConversationId;
    let effectiveOptions = options;
    const trimmedQuestion = question.trim().toLowerCase();
    const isManualReset = trimmedQuestion === 'reset';
    const activeFeedbackJourney = findActiveSelfHelpFeedbackJourney(messages);
    const pendingFeedbackSubmission =
      getPendingSelfHelpFeedbackSubmission(sessionIdRef.current);

    if (
      effectiveOptions.isSelfHelpTicketHandoff &&
      effectiveOptions.selfHelpFeedbackContext
    ) {
      persistPendingSelfHelpFeedbackSubmission(
        sessionIdRef.current,
        buildSelfHelpFeedbackFormAssociation(
          effectiveOptions.selfHelpFeedbackContext
        )
      );
    }

    if (
      !effectiveOptions.isSelfHelpTicketHandoff &&
      activeFeedbackJourney &&
      isIncompleteSelfHelpFeedback(activeFeedbackJourney.state)
    ) {
      const directTicketIntent = isDirectTicketIntent(question);
      const ticketHandoffReady =
        activeFeedbackJourney.state.status === 'ticket-ready' ||
        activeFeedbackJourney.state.status === 'fallback';

      if (directTicketIntent && ticketHandoffReady) {
        const context = buildSelfHelpFeedbackTicketContext(
          activeFeedbackJourney.descriptor,
          activeFeedbackJourney.state
        );
        emitSelfHelpFeedbackJourneyEvent({
          type: 'ticket-requested',
          journeyId: context.journeyId,
          selfHelpTurnId: context.selfHelpTurnId,
          validatorFallback: context.validatorFallback,
          clarificationUsed: context.clarificationUsed,
          primaryFailureReason: context.primaryFailureReason,
        });
        return handleSendMessage(
          activeFeedbackJourney.descriptor.ticketActionPrompt
            || activeFeedbackJourney.descriptor.copy.ticketActionPrompt,
          {
            selfHelpFeedbackContext: context,
            isSelfHelpTicketHandoff: true,
          }
        );
      } else if (directTicketIntent) {
        const blockedTicketMessage =
          activeFeedbackJourney.descriptor.copy.blockedTicketMessage;
        emitSelfHelpFeedbackJourneyEvent({
          type: 'ticket-bypass-blocked',
          journeyId: activeFeedbackJourney.state.journeyId,
          selfHelpTurnId: activeFeedbackJourney.descriptor.selfHelpTurnId,
        });
        const blockedMessage: Message = {
          id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ASSISTANT + Date.now(),
          role: 'assistant',
          content: blockedTicketMessage,
          timestamp: Date.now(),
          meta: {
            responseType: 'text',
          },
        };
        setMessages(previousMessages => {
          const lastMessage = previousMessages[previousMessages.length - 1];
          return lastMessage?.content === blockedTicketMessage
            ? previousMessages
            : [...previousMessages, blockedMessage];
        });
        return false;
      } else if (
        isManualReset ||
        !pendingFeedbackSubmission ||
        pendingFeedbackSubmission.journeyId !==
          activeFeedbackJourney.state.journeyId ||
        pendingFeedbackSubmission.selfHelpTurnId !==
          activeFeedbackJourney.descriptor.selfHelpTurnId
      ) {
        const abandoned = abandonIncompleteSelfHelpFeedback(
          messages,
          isManualReset ? 'reset' : 'unrelated-query'
        );
        if (abandoned.messages !== messages) {
          setMessages(abandoned.messages);
          abandoned.abandoned.forEach(event =>
            emitSelfHelpFeedbackJourneyEvent({ type: 'abandoned', ...event })
          );
        }
        if (isManualReset) {
          clearPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
        }
      }
    }

    // WI-1249082: If self-help button was shown but user is sending a new message
    // instead of clicking "Create Incident", that's a deflection success
    if (selfHelpButtonShown === true) {
      setSelfHelpButtonShown(false);
      window.dispatchEvent(new CustomEvent('self-help-deflection-success', {
        detail: { nextQuery: question, timestamp: new Date().toISOString() }
      }));
    }

    setFollowUpQuestions([]);
    setLoading(true);
    
    // Check if this is an exit confirmation
    const isExitConfirmation = showExitConfirmation ;
    
    // Clear exit confirmation state after handling
    if (isExitConfirmation) {
      setShowExitConfirmation(false);
    }
    
    // Check if this is a manual reset query
    const queryStartTime = Date.now();
    const hasFiles = uploadedFiles.length > 0;
    
    // Log telemetry
    mcapsTelemetry.logQuerySubmitted({
      query: question,
      component: 'SimpleCopilotPanel',
      action: 'submit'
    });

    // Add user message (uploadedFiles passed explicitly — was closure in original)
    const userMessage = createUserMessage(question, hasFiles, uploadedFiles);
    // Remember this as the active anchor question
    lastAnchoredMessageIdRef.current = userMessage.id;
    setMessages(prev => [...prev, userMessage]);

    try {
      let data: any;
      
      if (COPILOT_CONSTANTS.FEATURES.USE_MOCK_API) {
        data = mockApiResponse;
      } else {
        if (!effectiveApi) {
          throw new Error(COPILOT_CONSTANTS.API.ERROR_MESSAGES.API_NOT_CONFIGURED);
        }
        
        const ticketId = sessionStorage.getItem('mcapsCopilot_ticketNumber') ;
        let response: any;

        // Story 1236552: Check if conversational form session is active
        // formSessionActive comes from the latest bot message's meta
        const latestBotMsg = [...(messages || [])].reverse().find(m => m.role === 'assistant' && m.meta);
        const isFormSessionActive = (latestBotMsg?.meta as any)?.formSessionActive === true;

        // A feedback handoff always enters the normal ticket-intent route first.
        // Any files remain available for the returned form and its unchanged upload flow.
        if (effectiveOptions.isSelfHelpTicketHandoff) {
          response = await processTextOnly(
            question,
            false,
            effectiveOptions.selfHelpFeedbackContext
          );
        } else if (isExitConfirmation) {
          response = await processTextOnly(question, true);
        } else if (hasFiles) {
          if (isFormSessionActive && !ticketId) {
            // FORM-FILL MODE: Upload to blob via /attachment, then send text to /ask
            console.log('[Attachment] Routing to form-fill mode (conversational form session active)');
            response = await processFormFillFiles(
              uploadedFiles, question, isExitConfirmation, requestConversationId, isCurrent
            );
          } else if (question.trim()) {
            if(ticketId){
                response = await processFilesOnly(uploadedFiles, ticketId);
            }else{
              setUploadedFiles([]);
              globalUploadedFilesRef.current = [];
              response = await processTextOnly(question, isExitConfirmation);
            }
             
          } else {
            if (!ticketId) {
              const errorMessage: Message = {
                id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
                role: 'assistant',
                content: 'Ticket number not found to attach files.',
                timestamp: Date.now()
              };
              setMessages(prev => [...prev, errorMessage]);
              setUploadedFiles([]);
              globalUploadedFilesRef.current = [];
              return false;
            }
            response = await processFilesOnly(uploadedFiles, ticketId);
          }
        } else {
          response = await processTextOnly(
            question,
            isExitConfirmation,
            effectiveOptions.selfHelpFeedbackContext
          );
        }

        if (!isCurrent()) return false;
        validateAskResponse(response);
        if (!(response && response.status >= COPILOT_CONSTANTS.API.SUCCESS_STATUS_MIN && response.status < COPILOT_CONSTANTS.API.SUCCESS_STATUS_MAX)) {
          throw new Error('Something went wrong, Try again');
        }
        
        data = response.data || response;
      }

      // ========== RESPONSE NORMALIZATION ==========
      data = normalizeApiResponse(data, 'handleSendMessage');

      // ========== ADAPTIVE CARD DETECTION ==========
      // Check if this is a direct Adaptive Card response
      const isDirectAdaptiveCard = data && 
        typeof data === 'object' && 
        data.type === 'AdaptiveCard' && 
        data.version;
      let answerText: string;

      if (isDirectAdaptiveCard) {
        // Direct Adaptive Card response - no text content needed
        answerText = '';
      } else {
        // Regular response - extract text content
        answerText = data.Message || data.response || '';
      }

      const queryEndTime = Date.now();
      const processingTime = queryEndTime - queryStartTime;
      
      // Log response telemetry
      mcapsTelemetry.logResponseReceived({
        query: question,
        response: answerText || '[Adaptive Card Response]'
      });
      
      const ticketOutcome = readTicketOutcome(data);
      const confirmedTicketOutcome =
        isConfirmedTicketOutcome(ticketOutcome);
      const extractedTicketNumber = ticketOutcome
        ? confirmedTicketOutcome
          ? ticketOutcome.ticketNumber
          : null
        : extractTicketNumber(answerText || JSON.stringify(data), data);
      if (extractedTicketNumber) {
        setCurrentTicketNumber(extractedTicketNumber);
        sessionStorage.setItem('mcapsCopilot_ticketNumber', extractedTicketNumber);
      }
      
      // Format response and create bot message
      const formatted = formatAndConvertByType(data);
      const feedbackSubmission =
        effectiveOptions.selfHelpFeedbackContext
          ? buildSelfHelpFeedbackFormAssociation(
              effectiveOptions.selfHelpFeedbackContext
            )
          : getPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
      if (formatted.format === 'form' && feedbackSubmission) {
        formatted.dataToDisplay = {
          ...formatted.dataToDisplay,
          selfHelpFeedbackSubmission: feedbackSubmission,
        };
      }

      const completedFeedbackSubmission = extractedTicketNumber
        ? feedbackSubmission
        : undefined;
      if (completedFeedbackSubmission) {
        setMessages(previousMessages =>
          completeSelfHelpFeedbackJourney(
            previousMessages,
            completedFeedbackSubmission
          )
        );
        emitSelfHelpFeedbackJourneyEvent({
          type: 'ticket-submission-succeeded',
          ...completedFeedbackSubmission,
        });
      }

      if (
        confirmedTicketOutcome ||
        (!ticketOutcome && completedFeedbackSubmission) ||
        (formatted.format !== 'form' && data.formSessionActive === false)
      ) {
        clearPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
        if (currentActiveFormId) {
          setSubmittedFormIds(prev => new Set([...prev, currentActiveFormId]));
          setMessages(prev => prev.map(message => message.id === currentActiveFormId ? {
            ...message, meta: {
              ...message.meta, formSessionActive: false,
              dataToDisplay: {
                ...message.meta?.dataToDisplay,
                formJson: {
                  ...message.meta?.dataToDisplay?.formJson,
                  collectedData: { ...message.meta?.dataToDisplay?.formJson?.collectedData, ...persistentFormValues },
                },
              },
            },
          } : message));
        }
        setCurrentFormMetadata(null);
        setCurrentFormQuery('');
        setCurrentActiveFormId(null);
        setPersistentFormValues({});
        formUploadCache.current.clear();
      }

      const botMessage = createBotMessage(answerText, data, formatted);

      // Handle form metadata (skip for reset)
      if (!isManualReset && formatted.format === 'form' && formatted.dataToDisplay) {
        setCurrentFormMetadata(formatted.dataToDisplay);
        setCurrentFormQuery(question);
        setCurrentActiveFormId(botMessage.id);
        if (feedbackSubmission) {
          emitSelfHelpFeedbackJourneyEvent({
            type: 'ticket-form-rendered',
            ...feedbackSubmission,
          });
        }
      }

      if (
        effectiveOptions.isSelfHelpTicketHandoff &&
        formatted.format !== 'form' &&
        data.formSessionActive !== true &&
        ticketOutcome?.status !== 'retryableFailure' &&
        ticketOutcome?.status !== 'unknown'
      ) {
        clearPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
      }

      // Story 1236552: Clear stale ticketId when a new form session starts
      // Prevents file uploads from attaching to a previous ticket
      if (data.formSessionActive && !extractedTicketNumber) {
        sessionStorage.removeItem('mcapsCopilot_ticketNumber');
        setCurrentTicketNumber(null);
      }

      // If this was a manual reset query, add separator directly (skip bot message)
      if (isManualReset) {
        setFollowUpQuestions([]); // Clear follow-up questions
        setTimeout(() => {
          if (!isCurrent()) return;
          const separatorMessage: Message = {
            id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.SYSTEM + Date.now(),
            role: 'separator',
            content: COPILOT_CONSTANTS.MESSAGES.NEW_CONVERSATION,
            timestamp: Date.now(),
            showNewConversation: true,
            separator: 'NewConversation',
            from: 'newConversation'
          };
          setMessages(prev => [...prev, separatorMessage]);
          
          // Scroll to bottom to show separator
          setTimeout(() => {
            if (!isCurrent()) return;
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }, 50);
        }, 100);
      } else {
        // Add bot message for normal queries
        setMessages(prev => [...prev, botMessage]);
      }
      
      // Update follow-up questions
      const newFollowUpQuestions = data.followUpQuestions || data.followupQuestions || [];
      setFollowUpQuestions(newFollowUpQuestions);

      // Clear uploaded files after successful response
      if (hasFiles) {        
        setUploadedFiles([]);
        globalUploadedFilesRef.current = [];
      }

      return true;
    } catch (e: any) {
      if (!isCurrent()) return false;
      if (effectiveOptions.isSelfHelpTicketHandoff) {
        clearPendingSelfHelpFeedbackSubmission(sessionIdRef.current);
      }
      console.error('API Error:', e);
      mcapsTelemetry.logError({
        query: question,
        error: e.message
      });

      // Use SystemIssue message like legacy implementation
      const errorMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
        role: 'error',
        content: e instanceof RangeError || /draft has been retained/.test(e.message)
          ? e.message : COPILOT_CONSTANTS.MESSAGES.SYSTEM_ISSUE,
        timestamp: Date.now(),
        meta: {
          originalError: e.message,
          responseType: 'error'
        }
      };
      setMessages(prev => [...prev, errorMessage]);
      return false;
    } finally {
      if (isCurrent()) {
        setLoading(false);
     
      setTimeout(() => {
        if (!isCurrent()) return;
        if (controlRef.current?.focus) {
          controlRef.current.focus();
        }
      }, 100);
      }
    }
  }, [effectiveApi, userEmail, mcapsTelemetry, uploadedFiles, showExitConfirmation, messages, currentActiveFormId, currentFormMetadata, persistentFormValues]);

  const handleSelfHelpTicketReady = React.useCallback(
    async (
      context: SelfHelpFeedbackTicketContext,
      ticketActionPrompt: string
    ): Promise<boolean> => {
      emitSelfHelpFeedbackJourneyEvent({
        type: 'ticket-requested',
        journeyId: context.journeyId,
        selfHelpTurnId: context.selfHelpTurnId,
        validatorFallback: context.validatorFallback,
        clarificationUsed: context.clarificationUsed,
      });
      return handleSendMessage(ticketActionPrompt, {
        selfHelpFeedbackContext: context,
        isSelfHelpTicketHandoff: true,
      });
    },
    [handleSendMessage]
  );

  // Verbatim from SimpleCopilotPanel.tsx L1225-1240
  // Apply an optional initial prompt (e.g., from deep-link) once by auto-submitting it
  React.useEffect(() => {
    if (!initialPrompt || hasAppliedInitialPromptRef.current) {
      return;
    }

    hasAppliedInitialPromptRef.current = true;
    handleSendMessage(initialPrompt);
    if (onInitialPromptApplied) {
      try {
        onInitialPromptApplied();
      } catch {
        // Fail-safe: ignore errors from external callback
      }
    }
  }, [initialPrompt, handleSendMessage]);

  // Listen for guidance prompt button clicks (send-prompt-action://)
  // Programmatically sends the prompt as if user typed it
  React.useEffect(() => {
    const handler = ((e: CustomEvent) => {
      const message = e.detail?.message;
      if (message && !loading) {
        handleSendMessage(message);
      }
    }) as EventListener;
    window.addEventListener('copilot-send-message', handler);
    return () => window.removeEventListener('copilot-send-message', handler);
  }, [handleSendMessage, loading]);

  // Verbatim from SimpleCopilotPanel.tsx L1243-1245
  const handlePromptSelected = React.useCallback((prompt: string) => {
    controlRef.current?.setInputText(prompt);
  }, []);

  // Verbatim from SimpleCopilotPanel.tsx L1247-1249
  const handleRemoveMessage = React.useCallback((messageId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  }, []);

  return {
    handleSendMessage,
    handleSelfHelpTicketReady,
    handlePromptSelected,
    handleRemoveMessage,
  };
}

// Generated by Copilot
// updated on: 2026-09-14 | 7 lines changed | [Auto Generated] | Preserve descriptor routing and failure reason through ticket handoff | Time Taken 2 min
// updated on: 2026-09-18 | 48 lines changed | [Auto Generated] | Preserve GPS feedback telemetry through conversational form confirmation | Time Taken 7 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Routed ready direct intents through typed handoff and persisted safe form association | Time Taken 8 min
// updated on: 2026-09-09 | Story 1297509 TASK-009 | Added guarded feedback-context ticket intent handoff and form routing | Time Taken 12 min
// updated on: 2026-09-09 | Story 1297509 UI audit | Added a visible privacy-safe response when pre-readiness ticket bypass is blocked | Time Taken 4 min
// updated on: 2026-09-10 | Story 1297509 hardening | Sourced blocked and ticket-action messages from the active descriptor | Time Taken 3 min
// updated on: 2026-10-16 | [Auto Generated] | Terminalized confirmed form and self-help state from structured ticket outcomes | Time Taken 4 min
// updated on: 2026-10-18 | 17 lines changed | [Auto Generated] | Close successful feedback journeys and preserve ticket-reference lookup text | Time Taken 4 min
// Generated by Copilot
// updated on: 2026-10-26 | 16 lines added/updated/removed | [Auto Generated] | Echo generic hidden values through normal cancellation CSV without uploads | Time Taken 3 min
// Generated by Copilot
// updated on: 2026-10-26 | 70 lines added/updated/removed | [Auto Generated] | Validate raw and expanded asks preserve blob URIs and clear terminal form state | Time Taken 7 min
// Generated by Copilot
// updated on: 2026-10-26 | 25 lines added/updated/removed | [Auto Generated] | Retain conversational multi-file references across retries and honor schema limits | Time Taken 2 min
